import { FC } from 'react';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import { useTranslation } from 'react-i18next';
import { InboxItem, MessageType } from './types';
import moment from 'moment';
import styles from './inboxRow.module.scss';

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
  '#eab308', '#22c55e', '#06b6d4', '#3b82f6',
];

export const hashColor = (str: string): string => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
};

export const companyInitials = (name: string): string =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?';

// Supplier+project group identity — keep in sync with the backend's groupKey (inboxService.js).
export const groupKey = (supplierId: string, projectId: string): string => `${supplierId}__${projectId}`;

export const relativeTime = (iso: string, yesterday = 'Igår'): string => {
  const m = moment(iso);
  const now = moment();
  if (m.isSame(now, 'day')) return m.format('HH:mm');
  if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return yesterday;
  return m.format('D MMM');
};

// Single source of truth for message-type presentation: the i18n label (key +
// Swedish fallback) and the badge colours. The inbox filter options (InboxTab)
// derive from this too, so the badge label and the filter label can't drift.
export const TYPE_META: Record<MessageType, { labelKey: string; fallback: string; bg: string; color: string }> = {
  bidMessage:       { labelKey: 'inbox.type.bidMessage',       fallback: 'Anbud',      bg: '#dbeafe', color: '#1d4ed8' },
  intentionMessage: { labelKey: 'inbox.type.intentionMessage', fallback: 'Avsikt',     bg: '#ede9fe', color: '#7c3aed' },
  quotationMessage: { labelKey: 'inbox.type.quotationMessage', fallback: 'Förfrågan',  bg: '#ffedd5', color: '#ea580c' },
  standardMessage:  { labelKey: 'inbox.type.standardMessage',  fallback: 'Meddelande', bg: '#f3f4f6', color: '#6b7280' },
};

export const UnreadBadge: FC<{ count: number }> = ({ count }) => (
  <div className={styles.unreadBadge}>{count}</div>
);

// Shared square icon button used for row/panel actions (mark read/unread,
// create activity, close, attach file). Always stops propagation so it's
// safe to use inside a row that has its own onClick. Size/radius are dynamic
// so they stay inline; the rest lives in .iconButton.
type IconButtonProps = {
  onClick: () => void;
  icon: FC<{ style?: React.CSSProperties }>;
  title: string;
  size?: number;
  borderRadius?: number;
  iconSize?: number;
};

export const IconButton: FC<IconButtonProps> = ({
  onClick, icon: Icon, title, size = 30, borderRadius = 6, iconSize = 16,
}) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title={title}
    className={styles.iconButton}
    style={{ width: size, height: size, borderRadius }}
  >
    <Icon style={{ fontSize: iconSize }} />
  </button>
);

type InboxCardProps = {
  item: InboxItem;
  isSelected: boolean;
  onClick: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onCreateActivity: () => void;
};

const InboxCard: FC<InboxCardProps> = ({
  item, isSelected, onClick, onMarkRead, onMarkUnread, onCreateActivity,
}) => {
  const [t] = useTranslation('dashboard');
  const avatarColor = hashColor(item.supplierName || item.conversationId);
  const initials = companyInitials(item.supplierName);
  const typeInfo = TYPE_META[item.messageType] ?? TYPE_META.standardMessage;
  const isUnread = item.unreadCount > 0;

  const rowState = isSelected ? styles.rowSelected : (isUnread ? styles.rowUnread : '');

  return (
    <div
      data-inbox-row={item.conversationId}
      onClick={onClick}
      className={`${styles.row} ${styles.rowIndent} ${rowState}`}
    >
      {/* Avatar — company initials */}
      <div className={styles.avatar} style={{ backgroundColor: avatarColor }}>
        {initials}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Row 1: supplier name + type badge + project badge */}
        <div className={styles.nameRow}>
          <span className={`${styles.name} ${isUnread ? styles.nameUnread : ''}`}>
            {item.supplierName || '—'}
          </span>
          {/* Type badge — fixed position after supplier name */}
          <span className={styles.typeBadge} style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
            {t(typeInfo.labelKey, typeInfo.fallback)}
          </span>
          {item.projectName && (
            <span className={styles.projectBadge}>{item.projectName}</span>
          )}
        </div>

        {/* Row 2: area · sender · preview */}
        <div className={styles.detailRow}>
          {item.title && (
            <span className={styles.area}>{item.title}</span>
          )}
          {item.title && item.senderName && (
            <span className={styles.sep}>·</span>
          )}
          {item.senderName && (
            <span className={`${styles.sender} ${isUnread ? styles.senderUnread : ''}`}>
              {item.senderName}
            </span>
          )}
          {item.preview && (item.title || item.senderName) && (
            <span className={styles.sep}>·</span>
          )}
          <span className={styles.preview}>{item.preview}</span>
        </div>
      </div>

      {/* Unread dot + timestamp / hover actions */}
      <div className={styles.right}>
        {isUnread && <UnreadBadge count={item.unreadCount} />}
        <span className={styles.timestamp}>{relativeTime(item.updatedAt, t('inbox.time.yesterday', 'Igår'))}</span>
        <div className={styles.actions}>
          <IconButton
            onClick={() => item.unreadCount === 0 ? onMarkUnread() : onMarkRead()}
            icon={item.unreadCount === 0 ? MailOutlineIcon : DraftsOutlinedIcon}
            title={item.unreadCount === 0 ? t('inbox.markAsUnread', 'Markera som oläst') : t('inbox.markAsRead', 'Markera som läst')}
          />
          <IconButton onClick={onCreateActivity} icon={AddTaskOutlinedIcon} title={t('inbox.createActivity', 'Skapa aktivitet')} />
        </div>
      </div>
    </div>
  );
};

export default InboxCard;
